// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * System status enum for rights request cases.
 * See: docs/backend/workflows.md
 */
export const CaseStatus = {
    NEW: 'new',
    IN_REVIEW: 'in_review',
    WAITING_ON_REQUESTER: 'waiting_on_requester',
    WAITING_ON_INTERNAL_DATA: 'waiting_on_internal_data',
    APPROVED: 'approved',
    CLOSED: 'closed',
    REJECTED: 'rejected',
};
/** Terminal statuses that cannot transition to non-terminal statuses. */
export const TERMINAL_STATUSES = new Set([
    CaseStatus.APPROVED,
    CaseStatus.CLOSED,
    CaseStatus.REJECTED,
]);
/** Statuses where the SLA clock is paused. */
export const SLA_PAUSED_STATUSES = new Set([
    CaseStatus.WAITING_ON_REQUESTER,
    CaseStatus.WAITING_ON_INTERNAL_DATA,
]);
/**
 * Operational sub-status enum.
 * Each sub-status maps to exactly one system status.
 * See: docs/backend/workflows.md
 */
export const SubStatus = {
    // new
    INTAKE_RECEIVED: 'intake_received',
    PENDING_TRIAGE: 'pending_triage',
    // in_review
    IDENTITY_VERIFICATION: 'identity_verification',
    GATHERING_DATA: 'gathering_data',
    COMPILING_RESPONSE: 'compiling_response',
    LEGAL_REVIEW: 'legal_review',
    QUALITY_CHECK: 'quality_check',
    DELETION_IN_PROGRESS: 'deletion_in_progress',
    DELETION_VERIFICATION: 'deletion_verification',
    // waiting_on_requester
    AWAITING_IDENTITY_DOCUMENT: 'awaiting_identity_document',
    AWAITING_CLARIFICATION: 'awaiting_clarification',
    AWAITING_CONFIRMATION: 'awaiting_confirmation',
    // waiting_on_internal_data
    AWAITING_BU_RESPONSE: 'awaiting_bu_response',
    AWAITING_SYSTEM_EXPORT: 'awaiting_system_export',
    AWAITING_VENDOR_RESPONSE: 'awaiting_vendor_response',
    LEGAL_HOLD: 'legal_hold',
};
/** Maps each sub-status to its valid parent system status. */
export const SUB_STATUS_TO_STATUS = {
    [SubStatus.INTAKE_RECEIVED]: CaseStatus.NEW,
    [SubStatus.PENDING_TRIAGE]: CaseStatus.NEW,
    [SubStatus.IDENTITY_VERIFICATION]: CaseStatus.IN_REVIEW,
    [SubStatus.GATHERING_DATA]: CaseStatus.IN_REVIEW,
    [SubStatus.COMPILING_RESPONSE]: CaseStatus.IN_REVIEW,
    [SubStatus.LEGAL_REVIEW]: CaseStatus.IN_REVIEW,
    [SubStatus.QUALITY_CHECK]: CaseStatus.IN_REVIEW,
    [SubStatus.DELETION_IN_PROGRESS]: CaseStatus.IN_REVIEW,
    [SubStatus.DELETION_VERIFICATION]: CaseStatus.IN_REVIEW,
    [SubStatus.AWAITING_IDENTITY_DOCUMENT]: CaseStatus.WAITING_ON_REQUESTER,
    [SubStatus.AWAITING_CLARIFICATION]: CaseStatus.WAITING_ON_REQUESTER,
    [SubStatus.AWAITING_CONFIRMATION]: CaseStatus.WAITING_ON_REQUESTER,
    [SubStatus.AWAITING_BU_RESPONSE]: CaseStatus.WAITING_ON_INTERNAL_DATA,
    [SubStatus.AWAITING_SYSTEM_EXPORT]: CaseStatus.WAITING_ON_INTERNAL_DATA,
    [SubStatus.AWAITING_VENDOR_RESPONSE]: CaseStatus.WAITING_ON_INTERNAL_DATA,
    [SubStatus.LEGAL_HOLD]: CaseStatus.WAITING_ON_INTERNAL_DATA,
};
/**
 * Valid status transitions.
 * Key = from status, Value = set of allowed target statuses.
 * See: docs/backend/workflows.md - Transition Rules
 */
export const VALID_TRANSITIONS = {
    [CaseStatus.NEW]: new Set([CaseStatus.IN_REVIEW]),
    [CaseStatus.IN_REVIEW]: new Set([
        CaseStatus.WAITING_ON_REQUESTER,
        CaseStatus.WAITING_ON_INTERNAL_DATA,
        CaseStatus.APPROVED,
        CaseStatus.CLOSED,
        CaseStatus.REJECTED,
    ]),
    [CaseStatus.WAITING_ON_REQUESTER]: new Set([
        CaseStatus.IN_REVIEW,
        CaseStatus.REJECTED,
        CaseStatus.CLOSED,
    ]),
    [CaseStatus.WAITING_ON_INTERNAL_DATA]: new Set([
        CaseStatus.IN_REVIEW,
        CaseStatus.CLOSED,
    ]),
    [CaseStatus.APPROVED]: new Set(),
    [CaseStatus.CLOSED]: new Set(),
    [CaseStatus.REJECTED]: new Set(),
};
//# sourceMappingURL=status.js.map